/**
 * Pruefung des Umleitungsziels nach der Anmeldung.
 *
 * DER BEFUND (login#3): createRedirectLink baute das Ziel so:
 *
 *   href = new URL(decodeURIComponent(this.$route.params.redirect));
 *   href.searchParams.append("token", localStorage.getItem("token"));
 *   window.location.replace(href.toString());
 *
 * Das Ziel kam ungeprueft aus der Route, und der Anmeldetoken wurde daran
 * angehaengt. Wer jemanden dazu bringt, /login/<kodierte-fremde-adresse>
 * zu oeffnen, bekommt nach dessen Anmeldung den Token an den eigenen Server
 * geschickt - im Klartext, in der Adresszeile.
 *
 * Der Angriff braucht keinen Zugriff auf irgendetwas: ein Link genuegt. Und er
 * sieht fuer den Angemeldeten unauffaellig aus, weil die Anmeldung selbst
 * ganz normal ablaeuft.
 *
 * Deshalb: nur eigene Adressen. Ein fremdes Ziel wird nicht etwa bereinigt,
 * sondern durch die Standardadresse ersetzt - der Nutzer landet in der
 * Anwendung, nur eben in unserer.
 */

/**
 * Eigene Adressen: genau die Hosts der Anwendung, ueber https - keine
 * Wildcard (dispatchgpt#30, Restpunkt L1; Entscheidung Moritz 18.10.2026).
 *
 * Vorher galt jede Subdomain von ondispatch.de (support., uptime., ...). Die
 * Weiterleitung traegt den loginCode, der sich 60 s lang gegen ein Token
 * tauschen laesst - eine Subdomain mit fremdem Inhalt, eigener Weiterleitung
 * oder uebernommenem DNS-Eintrag haette ihn abgreifen koennen. Ziel einer
 * Anmeldung ist aber immer die Anwendung selbst (root-config):
 *
 *   my.ondispatch.de      root-config k8s/prod/ingress.yaml
 *   dev-my.ondispatch.de  root-config k8s/dev/ingress.yaml, baseRedirectServer
 *
 * Neue Umgebungen kommen hier dazu - mit Test.
 */
export const EIGENE_HOSTS = Object.freeze(["my.ondispatch.de", "dev-my.ondispatch.de"]);

/**
 * localhost (jeder Port) nur in der Entwicklung (vue-cli-service serve, Tests)
 * - nicht im gebauten Bundle, das auf dev- und prod-login laeuft (Restpunkt
 * L2). vue-cli ersetzt process.env.NODE_ENV beim Bauen durch "production".
 */
const ENTWICKLUNG = /^localhost$/;
export function localhostErlaubt() {
  return process.env.NODE_ENV !== "production";
}

/**
 * Prueft, ob ein Ziel eine eigene Adresse ist.
 *
 * @param {string} ziel - die zu pruefende Adresse
 * @returns {boolean} true, wenn dorthin umgeleitet werden darf
 */
export function istEigenesZiel(ziel) {
  if (typeof ziel !== "string" || ziel === "") {
    return false;
  }

  let url;
  try {
    url = new URL(ziel);
  } catch (e) {
    //keine gueltige Adresse - etwa ein relativer Pfad oder Unsinn
    return false;
  }

  //javascript:, data: und Verwandte kommen hier gar nicht erst weiter
  if (url.protocol !== "https:" && url.protocol !== "http:") {
    return false;
  }

  //Zugangsdaten in der Adresse: https://my.ondispatch.de@boese.example zeigt
  //auf boese.example, sieht aber aus wie eine eigene Adresse
  if (url.username !== "" || url.password !== "") {
    return false;
  }

  const host = url.hostname.toLowerCase();

  if (ENTWICKLUNG.test(host)) {
    return localhostErlaubt();
  }

  //auf den eigenen Hosts nur https - sonst laesst sich der Code im Klartext
  //mitlesen, und genau darum geht es hier. Exakter Vergleich, kein Muster.
  return url.protocol === "https:" && EIGENE_HOSTS.includes(host);
}

/**
 * Liefert das Ziel, zu dem umgeleitet werden darf.
 *
 * @param {*} ziel - der Wert aus der Route, moeglicherweise URL-kodiert
 * @param {string} standard - die Standardadresse aus der Konfiguration
 * @returns {string} das gepruefte Ziel oder die Standardadresse
 */
export function sicheresZiel(ziel, standard) {
  //Die Route traegt bei fehlendem Ziel die 1 - als Zahl oder als Zeichenkette,
  //je nachdem, ob sie aus dem Router oder aus der Adresszeile kommt.
  if (ziel === undefined || ziel === null || ziel === 1 || ziel === "1") {
    return standard;
  }

  let entpackt;
  try {
    entpackt = decodeURIComponent(String(ziel));
  } catch (e) {
    //ungueltige Kodierung, etwa ein einzelnes %
    return standard;
  }

  if (!istEigenesZiel(entpackt)) {
    //Bewusst nur auf der Konsole und ohne Aufhebens: ein fremdes Ziel ist im
    //Regelfall ein Angriffsversuch, und der Angreifer soll daraus nichts
    //lernen. Der Nutzer landet in der Anwendung.
    console.warn("[onDispatch] Umleitungsziel abgewiesen:", entpackt);
    return standard;
  }

  return entpackt;
}

export default sicheresZiel;
